import React, { useState, useEffect } from 'react';

const MemoryMatchMath = () => {
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedCards, setMatchedCards] = useState([]);
  const [moves, setMoves] = useState(0);
  const [timeLeft, setTimeLeft] = useState(120);

  // Unlimited card pairs by level
  const generateCardPairs = (lvl) => {
    const pairCount = Math.min(4 + lvl, 12); // 5 to 13 pairs
    
    const mathPairs = [
      { id: 1, content: '2+3', match: '5' },
      { id: 2, content: '4×2', match: '8' },
      { id: 3, content: '10÷2', match: '5' },
      { id: 4, content: '7-3', match: '4' },
      { id: 5, content: '3×3', match: '9' },
      { id: 6, content: '15÷3', match: '5' },
      { id: 7, content: '6+6', match: '12' },
      { id: 8, content: '20-8', match: '12' },
      { id: 9, content: '5×4', match: '20' },
      { id: 10, content: '16÷4', match: '4' },
      { id: 11, content: '9+7', match: '16' },
      { id: 12, content: '8×2', match: '16' },
      { id: 13, content: '25÷5', match: '5' },
      { id: 14, content: '12-4', match: '8' },
      { id: 15, content: '6×3', match: '18' },
      { id: 16, content: '24÷3', match: '8' },
      { id: 17, content: '11+9', match: '20' },
      { id: 18, content: '30-10', match: '20' },
      { id: 19, content: '7×2', match: '14' },
      { id: 20, content: '28÷2', match: '14' },
    ];

    // Select random pairs
    const selectedPairs = [];
    const usedIndices = new Set();
    
    while (selectedPairs.length < pairCount && selectedPairs.length < mathPairs.length) {
      const idx = Math.floor(Math.random() * mathPairs.length);
      if (!usedIndices.has(idx)) {
        usedIndices.add(idx);
        selectedPairs.push(mathPairs[idx]);
      }
    }

    // Create card array with both question and answer
    const cardArray = [];
    selectedPairs.forEach((pair, idx) => {
      cardArray.push({
        id: `q${idx}`,
        content: pair.content,
        matchId: pair.id,
        type: 'question'
      });
      cardArray.push({
        id: `a${idx}`,
        content: pair.match,
        matchId: pair.id,
        type: 'answer'
      });
    });

    // Shuffle cards
    for (let i = cardArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [cardArray[i], cardArray[j]] = [cardArray[j], cardArray[i]];
    }

    return cardArray;
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setMoves(0);
    setTimeLeft(120);
    setFlippedCards([]);
    setMatchedCards([]);
    setCards(generateCardPairs(1));
  };

  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            setGameState('gameover');
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameState, timeLeft]);

  useEffect(() => {
    if (flippedCards.length === 2) {
      setMoves(m => m + 1);
      
      const [first, second] = flippedCards;
      const firstCard = cards.find(c => c.id === first);
      const secondCard = cards.find(c => c.id === second);

      if (firstCard.matchId === secondCard.matchId) {
        // Match found!
        setMatchedCards([...matchedCards, first, second]);
        setScore(s => s + 100 * level);
        setFlippedCards([]);
        
        // Check if all matched
        if (matchedCards.length + 2 === cards.length) {
          setTimeout(() => {
            setLevel(l => l + 1);
            setCards(generateCardPairs(level + 1));
            setMatchedCards([]);
            setFlippedCards([]);
            setTimeLeft(t => t + 30);
          }, 1000);
        }
      } else {
        // No match
        setTimeout(() => {
          setFlippedCards([]);
        }, 1000);
      }
    }
  }, [flippedCards]);

  const flipCard = (cardId) => {
    if (flippedCards.length >= 2 || flippedCards.includes(cardId) || matchedCards.includes(cardId)) {
      return;
    }
    setFlippedCards([...flippedCards, cardId]);
  };

  const isCardFlipped = (cardId) => {
    return flippedCards.includes(cardId) || matchedCards.includes(cardId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 to-purple-100 p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-8 text-indigo-800">
          🧠 Memory Match Math 🎴
        </h1>

        {gameState === 'menu' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🎴</div>
            <h2 className="text-3xl font-bold mb-4 text-gray-800">Match Math Problems!</h2>
            <p className="text-xl mb-8 text-gray-600">
              Find matching pairs of math problems and answers<br/>
              Test your memory and math skills!
            </p>
            <div className="bg-indigo-50 p-6 rounded-lg mb-8">
              <h3 className="font-bold text-lg mb-3">How to Play:</h3>
              <div className="text-left space-y-2">
                <div>1. Click cards to flip them</div>
                <div>2. Match math problems with their answers</div>
                <div>3. Complete all pairs to advance</div>
                <div>4. More cards each level!</div>
              </div>
            </div>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              START GAME
            </button>
          </div>
        )}

        {gameState === 'playing' && (
          <div>
            <div className="bg-white rounded-lg p-4 mb-4 flex justify-between items-center">
              <div className="text-xl font-bold">Score: {score}</div>
              <div className="text-xl font-bold">Level: {level}</div>
              <div className="text-xl font-bold">Moves: {moves}</div>
              <div className="text-xl font-bold">
                Matched: {matchedCards.length / 2}/{cards.length / 2}
              </div>
              <div className={`text-xl font-bold ${timeLeft < 30 ? 'text-red-600 animate-pulse' : 'text-green-600'}`}>
                Time: {timeLeft}s
              </div>
            </div>

            <div className={`grid gap-4 ${
              cards.length <= 12 ? 'grid-cols-4' : 
              cards.length <= 20 ? 'grid-cols-5' : 
              'grid-cols-6'
            }`}>
              {cards.map((card) => (
                <div
                  key={card.id}
                  onClick={() => flipCard(card.id)}
                  className={`aspect-square rounded-lg cursor-pointer transform transition-all duration-300 ${
                    isCardFlipped(card.id)
                      ? 'bg-gradient-to-br from-blue-400 to-purple-500 text-white scale-105'
                      : 'bg-gradient-to-br from-gray-300 to-gray-400 hover:scale-105'
                  } flex items-center justify-center text-2xl font-bold shadow-lg`}
                >
                  {isCardFlipped(card.id) ? (
                    <div className="text-center p-2">
                      {card.content}
                    </div>
                  ) : (
                    <div className="text-4xl">?</div>
                  )}
                </div>
              ))}
            </div>

            <div className="text-center mt-6 text-gray-700 text-lg">
              Click cards to reveal • Match problems with answers!
            </div>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🏆</div>
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Game Complete!</h2>
            <div className="text-3xl mb-4 text-indigo-600 font-bold">Final Score: {score}</div>
            <div className="text-2xl mb-2 text-gray-600">Level Reached: {level}</div>
            <div className="text-2xl mb-8 text-gray-600">Total Moves: {moves}</div>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              PLAY AGAIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemoryMatchMath;
